import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useEffect, useRef, useState } from 'react';
import type { AnimationItem } from 'lottie-web';
import type { LottieViewProps, LottieViewRef } from './LottieView.data';
import styles from './LottieView.module.scss';
import { lottieConfig } from '../../../data/lottie.config';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import dynamic from 'next/dynamic';

// https://github.com/airbnb/lottie-web/issues/3047"
const Player = dynamic(
  () => import('@lottiefiles/react-lottie-player').then((mod) => mod.Player),
  { ssr: false }, // disable SSR since the component might use browser APIs
);

export default function LottieView({
  id,
  loop = true,
  pauseWhenOutViewport = true,
  autoplay = false,
  className,
  children,
  ref,
}: LottieViewProps & { ref?: Ref<LottieViewRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<LottieViewRef | null>)?.current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;

  const lottieRef = useRef<AnimationItem>(null);
  const lottieData = lottieConfig[id];

  const [isLoaded, setIsLoaded] = useState(false);
  const [autoPlay, setAutoplay] = useState(autoplay ?? false);
  const [forcePlayAction, setForcePlayAction] = useState(false);

  const intersectionRef = useRef(null);
  const isVisibleInViewport = useIntersectionObserver(
    intersectionRef,
    {
      threshold: [0, 1],
    },
    pauseWhenOutViewport,
  );

  useEffect(() => {
    setAutoplay(autoplay);
  }, [autoplay]);

  useEffect(() => {
    setForcePlayAction(
      autoPlay && ((pauseWhenOutViewport && isVisibleInViewport) || !pauseWhenOutViewport),
    );
  }, [autoPlay, isVisibleInViewport, pauseWhenOutViewport]);

  useEffect(() => {
    if (forcePlayAction) {
      lottieRef?.current?.play();
    } else {
      lottieRef?.current?.pause();
    }
  }, [isLoaded, forcePlayAction, lottieRef]);

  const setProgress = useCallback(
    (progress: number) => {
      const totalFrames = lottieRef?.current?.getDuration(true) || 0;
      const newFrame = (totalFrames - 1) * progress;
      const currentFrame = lottieRef?.current?.currentFrame || 0;

      if (currentFrame !== newFrame) {
        lottieRef.current?.goToAndStop(newFrame, true);
      }
    },
    [lottieRef],
  );

  const play = useCallback(() => {
    setAutoplay(true);
    lottieRef.current?.play();
  }, [lottieRef]);

  const pause = useCallback(() => {
    setAutoplay(false);
    lottieRef.current?.pause();
  }, [lottieRef]);

  useImperativeHandle(
    ref,
    () => ({
      play,
      pause,
      setProgress,
      elementRef,
    }),
    [play, pause, setProgress, elementRef],
  );

  // todo adjust settings/props once we need more, such as:
  // - on 'intro' complete -> look for 'start_loop/end_loop' markers and start the looping segments.
  // todo: for now, we assume only a looping animation directly.

  return (
    <div ref={elementRef} className={classNames(styles.lottieView, className)}>
      {children}
      <div ref={intersectionRef as RefObject<HTMLDivElement | null>} className="abs-fill" />
      {lottieData && (
        <Player
          lottieRef={(instance) => {
            lottieRef.current = instance;
          }}
          src={lottieData.path}
          autoplay={false}
          loop={loop}
          onEvent={(event) => {
            if (event === 'load') setIsLoaded(true);
          }}
        />
      )}
    </div>
  );
}
